import { FC, useEffect, useMemo } from "react";
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { useNavigate } from "react-router-dom";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { AddCustomerModal } from "../modals/AddCustomerModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { faAdd, faPeopleGroup, faRefresh, faStar } from "@fortawesome/free-solid-svg-icons";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";

export const Customers: FC = () => {
  const { partnerClient } = useApiClient(config);
  const { resource, reload } = useFetchedResource(() => partnerClient.getAllCustomers());
  useEffect(() => {
    reload();
  }, []);

  const { can } = usePermissions(partnerClient);

  const customers: Customer[] = useMemo(() => {
    if (resource?.success === true && resource.data) {
      return resource.data;
    }
    return [];
  }, [resource]);
  return (
    <ContentContainer>
      <PageIconHeader icon={faPeopleGroup}>
        <Breadcrumbs crumbs={[]} currentPage={"Customers"} />
        <PageHeader>Customers</PageHeader>
      </PageIconHeader>
      {can("create", "customers") && <AddCustomerForm />}
      <CustomerOverview customers={customers} reloadCustomers={reload} />
    </ContentContainer>
  );
};

const AddCustomerForm: FC = () => {
  const { open: openCreateModal } = useModal({ title: "Add new customer", body: <AddCustomerModal /> });
  return (
    <Callout>
      <SectionHeader>Add new customer</SectionHeader>
      <Paragraph>
        Domains, sites and other resources are registered under a customer. You always have your own "internal" customer
        for internal resources, this customer can never be deleted.
      </Paragraph>
      <Button size={"sm"} type={"primary"} text={"New customer"} icon={faAdd} onClick={() => openCreateModal()} />
    </Callout>
  );
};

const CustomerOverview: FC<{ customers: Customer[]; reloadCustomers: () => void }> = (props) => {
  const navigate = useNavigate();
  const customers: Customer[] = useMemo(() => {
    return props.customers.sort((a, b) => (a.isTenantOwner ? -1 : a.displayName.localeCompare(b.displayName)));
  }, [props.customers]);
  return (
    <div className={"my-8"}>
      <SectionHeader>Your customers</SectionHeader>
      <FilterableDataTable
        keyProperty={"id"}
        data={customers}
        searchableProperties={["displayName", "referenceNumber"]}
        actions={<IconButton icon={faRefresh} onClick={props.reloadCustomers} type={"secondary"} size={"sm"} />}
        columns={[
          {
            header: "Name",
            property: "displayName",
            transform: (value, row) => (
              <span>
                {value}{" "}
                {row.isTenantOwner && (
                  <span
                    className={
                      "mx-2 px-2 py-1 text-brand-600 border border-brand-400 font-medium text-sm rounded-sm"
                    }
                  >
                    <FontAwesomeIcon icon={faStar} className={"mr-1"} />
                    You
                  </span>
                )}
              </span>
            ),
            sort: true,
          },
          {
            header: "Customer number",
            property: "referenceNumber",
          },
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/customers/${row.id}`, e)}
      />
    </div>
  );
};
